import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { zipcodesAPI } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import './Zipcodes.css';

const Zipcodes = () => {
  const [zipcodes, setZipcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('approvedCount');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchZipcodes();
  }, []);

  const fetchZipcodes = async () => {
    try {
      setLoading(true);
      const response = await zipcodesAPI.getZipcodes();
      // console.log('Zipcodes API Response:', response.data); // Debug log
      if (response.data.success) {
        setZipcodes(response.data.data.zipcodes);
        setStats(response.data.data.summary);
      }
    } catch (error) {
      console.error('Error fetching zipcodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredZipcodes = zipcodes.filter(zipcode => {
    const searchLower = searchTerm.toLowerCase();
    return (
      zipcode.zipCode.toLowerCase().includes(searchLower) ||
      zipcode.states.some(state => state.toLowerCase().includes(searchLower)) ||
      zipcode.cities.some(city => city.toLowerCase().includes(searchLower))
    );
  });

  const sortedZipcodes = [...filteredZipcodes].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    if (sortBy === 'states' || sortBy === 'cities') {
      aValue = a[sortBy]?.[0] || '';
      bValue = b[sortBy]?.[0] || '';
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="zipcodes-container">
      <div className="zipcodes-header">
        <h1>Zipcodes Management</h1>
        <p>Manage and analyze bookkeeper distribution by geographic areas</p>
      </div>

      {stats && (
        <div className="zipcodes-stats">
          <div className="stat-card">
            <h3>{stats.totalZipcodes}</h3>
            <p>Total Zipcodes</p>
          </div>
          <div className="stat-card">
            <h3>{stats.totalBookkeepers}</h3>
            <p>Total Bookkeepers</p>
          </div>
          <div className="stat-card">
            <h3>{stats.totalApproved}</h3>
            <p>Active Bookkeepers</p>
          </div>
          <div className="stat-card">
            <h3>{stats.totalPending}</h3>
            <p>Pending Bookkeepers</p>
          </div>
        </div>
      )}

      <div className="zipcodes-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search zipcodes, states, or cities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="sort-controls">
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="approvedCount">By Approved</option>
            <option value="pendingCount">By Pending</option>
            <option value="bookkeeperCount">By Total</option>
            <option value="zipCode">By Zipcode</option>
            <option value="states">By State</option>
          </select>
          <button 
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      <div className="zipcodes-table-container">
        <table className="zipcodes-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('zipCode')}>
                Zipcode {sortBy === 'zipCode' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('states')}>
                State {sortBy === 'states' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('cities')}>
                City {sortBy === 'cities' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('approvedCount')}>
                Total Approved {sortBy === 'approvedCount' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('pendingCount')}>
                Total Pending {sortBy === 'pendingCount' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Status Breakdown</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedZipcodes.map((zipcode) => (
              <tr key={zipcode.zipCode} className="zipcode-row">
                <td className="zipcode-cell">
                  <strong>{zipcode.zipCode}</strong>
                </td>
                <td className="state-cell">
                  {zipcode.states.join(', ')}
                </td>
                <td className="city-cell">
                  {zipcode.cities.slice(0, 2).join(', ')}
                  {zipcode.cities.length > 2 && ` +${zipcode.cities.length - 2} more`}
                </td>
                <td className="count-cell approved-count">
                  <span className="count-badge approved">
                    {zipcode.approvedCount || 0}
                  </span>
                </td>
                <td className="count-cell pending-count">
                  <span className="count-badge pending">
                    {zipcode.pendingCount || 0}
                  </span>
                </td>
                <td className="status-cell">
                  <div className="status-breakdown">
                    <span 
                      className="status-dot approved" 
                      title={`${zipcode.approvedCount || 0} Approved`}
                    >
                      {zipcode.approvedCount || 0}
                    </span>
                    <span 
                      className="status-dot pending" 
                      title={`${zipcode.pendingCount || 0} Pending`}
                    >
                      {zipcode.pendingCount || 0}
                    </span>
                    <span 
                      className="status-dot expired" 
                      title={`${zipcode.expiredCount || 0} Expired/Rejected`}
                    >
                      {zipcode.expiredCount || 0}
                    </span>
                  </div>
                </td>
                <td className="actions-cell">
                  <Link 
                    to={`/bookkeepers-by-zipcode/${zipcode.zipCode}`}
                    className="view-btn"
                  >
                    View Bookkeepers
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sortedZipcodes.length === 0 && (
          <div className="no-results">
            <p>No zipcodes found matching your search criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Zipcodes;